import React, { useState, useCallback } from "react";
import { Text } from "@chakra-ui/react";
import { FiUpload } from "react-icons/fi";
import { Button } from "newui/components/Button";
import SkeletonBase from "newui/components/Skeleton";
import { RevisionUploadAccordion } from "./RevisionUploadAccordion";
import { UploadRevisionModal } from "./UploadRevisionModal";
import {
	useRevisionUploads,
	useUploadRevision,
} from "../hooks/useRevisionUploads";
import type {
	EvaluationRevisionSupplier,
	UploadRevisionPayload,
} from "../types/revision-review.type";

interface RevisionUploadsTabProps {
	evaluationId: number;
	rfxId: number;
	suppliers: EvaluationRevisionSupplier[];
}

export const RevisionUploadsTab: React.FC<RevisionUploadsTabProps> = ({
	rfxId,
	evaluationId,
	suppliers,
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);

	const { data: uploads, isLoading } = useRevisionUploads(rfxId, evaluationId);
	const uploadMutation = useUploadRevision(rfxId, evaluationId);

	const handleUploadSubmit = useCallback(
		(payload: UploadRevisionPayload) => {
			uploadMutation.mutate(payload, {
				onSuccess: () => {
					setIsModalOpen(false);
				},
			});
		},
		[uploadMutation]
	);

	if (isLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase
					startColor="lightgray"
					width="100%"
					height="48px"
					className="!rounded-lg"
				/>
				<SkeletonBase
					startColor="lightgray"
					width="100%"
					height="48px"
					className="!rounded-lg"
				/>
			</div>
		);
	}

	return (
		<div data-testid="revision-uploads-tab">
			<div className="mb-4 flex items-center justify-between">
				<Text className="text-sm text-gray-500">
					Revision files uploaded by the lead evaluator.
				</Text>
				<Button
					btntype="primary"
					size="sm"
					onClick={() => setIsModalOpen(true)}
					data-testid="upload-revision-btn"
				>
					<FiUpload className="mr-1.5 h-4 w-4" />
					Upload Revision
				</Button>
			</div>

			<RevisionUploadAccordion uploads={uploads ?? []} />

			<UploadRevisionModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				onSubmit={handleUploadSubmit}
				isLoading={uploadMutation.isPending}
				suppliers={suppliers}
			/>
		</div>
	);
};
